<template>
  <div class="p-6 dark:text-gray-50" v-if="canAdd || user?.is_staff">
    <div class="flex space-x-3 mb-6">
      <button
        @click="goBack"
        class="bg-gray-600 text-white px-4 py-1.5 rounded-md hover:bg-gray-700 text-sm flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
        </svg>
        Back
      </button>
      <h1 class="text-2xl font-semibold dark:text-white">{{ isEditMode ? 'Edit' : 'Add' }} From-Probe Entry</h1>
    </div>

    <!-- Page-level Error Banner -->
    <div v-if="pageError" class="bg-red-50 dark:bg-red-900/20 border border-red-400 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4 flex items-center">
      <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>
      <span class="font-medium">{{ pageError }}</span>
    </div>

    <form @submit.prevent="submitForm" class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <!-- Fortigate IP -->
      <div class="flex flex-col">
        <label for="fortigate_ip" class="mb-1 font-medium">
          Fortigate IP<span class="text-red-500">*</span>
        </label>
        <input
          id="fortigate_ip"
          v-model="formData.fortigate_ip"
          type="text"
          placeholder="e.g., 192.168.1.1"
          :class="inputClass('fortigate_ip')"
        />
        <FieldError :message="fieldErrors.fortigate_ip" />
      </div>

      <!-- SSH Port -->
      <div class="flex flex-col">
        <label for="ssh_port" class="mb-1 font-medium">SSH Port</label>
        <input
          id="ssh_port"
          v-model.number="formData.ssh_port"
          type="number"
          min="1"
          max="65535"
          :class="inputClass('ssh_port')"
        />
        <FieldError :message="fieldErrors.ssh_port" />
      </div>

      <!-- Username -->
      <div class="flex flex-col">
        <label for="username" class="mb-1 font-medium">
          SSH Username<span class="text-red-500">*</span>
        </label>
        <input
          id="username"
          v-model="formData.username"
          type="text"
          autocomplete="off"
          spellcheck="false"
          :class="inputClass('username')"
        />
        <FieldError :message="fieldErrors.username" />
      </div>

      <!-- Auth method selector -->
      <div class="flex flex-col">
        <span class="mb-1 font-medium">Authentication method<span class="text-red-500">*</span></span>
        <div class="flex items-center space-x-6">
          <label class="inline-flex items-center">
            <input type="radio" v-model="formData.auth_method" value="password" class="mr-2" />
            Password
          </label>
          <label class="inline-flex items-center">
            <input type="radio" v-model="formData.auth_method" value="key" class="mr-2" />
            Private key
          </label>
        </div>
      </div>

      <!-- Password block -->
      <div v-if="formData.auth_method === 'password'" class="flex flex-col">
        <label for="password" class="mb-1 font-medium">
          Password<span class="text-red-500" v-if="!isEditMode">*</span>
        </label>
        <div class="flex items-center space-x-2">
          <input
            id="password"
            v-model="formData.password"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="off"
            spellcheck="false"
            :placeholder="isEditMode ? 'Leave blank to keep existing' : ''"
            :class="inputClass('password') + ' flex-1'"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-sm rounded hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            {{ showPassword ? 'Hide' : 'Show' }}
          </button>
        </div>
        <FieldError :message="fieldErrors.password" />
      </div>

      <!-- Key block -->
      <div v-if="formData.auth_method === 'key'" class="flex flex-col">
        <label for="private_key" class="mb-1 font-medium">
          Private key (OpenSSH / PEM)<span class="text-red-500" v-if="!isEditMode">*</span>
        </label>
        <textarea
          id="private_key"
          v-model="formData.private_key"
          rows="6"
          autocomplete="off"
          spellcheck="false"
          :placeholder="isEditMode ? 'Leave blank to keep existing' : '-----BEGIN OPENSSH PRIVATE KEY-----\n...\n-----END OPENSSH PRIVATE KEY-----'"
          :class="inputClass('private_key') + ' font-mono text-sm'"
        ></textarea>
        <div class="mt-1">
          <input
            ref="keyFileInput"
            type="file"
            accept=".pem,.key,.openssh,application/octet-stream,text/plain"
            class="hidden"
            @change="handleKeyFile"
          />
          <button
            type="button"
            @click="$refs.keyFileInput.click()"
            class="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Load from file
          </button>
        </div>
        <FieldError :message="fieldErrors.private_key" />

        <label for="private_key_passphrase" class="mt-3 mb-1 font-medium">
          Key passphrase (optional)
        </label>
        <div class="flex items-center space-x-2">
          <input
            id="private_key_passphrase"
            v-model="formData.private_key_passphrase"
            :type="showPassphrase ? 'text' : 'password'"
            autocomplete="off"
            spellcheck="false"
            :placeholder="isEditMode ? 'Leave blank to keep existing' : ''"
            :class="inputClass('private_key_passphrase') + ' flex-1'"
          />
          <button
            type="button"
            @click="showPassphrase = !showPassphrase"
            class="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-sm rounded hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            {{ showPassphrase ? 'Hide' : 'Show' }}
          </button>
        </div>
        <FieldError :message="fieldErrors.private_key_passphrase" />
      </div>

      <!-- Collection Interval -->
      <div class="flex flex-col">
        <label for="collection_interval" class="mb-1 font-medium">
          Collection Interval (seconds)
        </label>
        <input
          id="collection_interval"
          v-model.number="formData.collection_interval"
          type="number"
          min="60"
          :class="inputClass('collection_interval')"
        />
        <FieldError :message="fieldErrors.collection_interval" />
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          @click="goBack"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {{ isEditMode ? 'Update' : 'Create' }} From-Probe Entry
        </button>
      </div>
    </form>
  </div>

  <PageError v-else-if="permsLoaded" :error="'Permission denied'" :errorCode="'403'" />
</template>

<script>
import { createSSHCollectorTarget, getSSHCollectorTargetById, updateSSHCollectorTarget } from "@/services/sshCollectorService";
import FieldError from "@/components/FieldError.vue";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";

const SENSITIVE_FIELDS = ["password", "private_key", "private_key_passphrase"];

export default {
  name: "AddSSHCollectorTarget",
  components: { FieldError, PageError },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      fieldErrors: {},
      pageError: null,
      showPassword: false,
      showPassphrase: false,
      formData: {
        fortigate_ip: "",
        ssh_port: 22,
        username: "splitpoint-ssh",
        auth_method: "password",
        password: "",
        private_key: "",
        private_key_passphrase: "",
        collection_interval: 3600,
      },
      canAdd: false,
    };
  },
  computed: {
    currentSchema() { return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null; },
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    configId() {
      return this.$route.params.configId;
    },
    jobId() {
      return this.$route.params.jobId;
    },
    isEditMode() {
      return !!this.jobId;
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.checkPermissions();
    if (this.isEditMode) {
      await this.loadExistingJob();
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canAdd = await useStore.hasPermission("Can add SSH Collector Config");
      }
    },
    async loadExistingJob() {
      try {
        const job = await getSSHCollectorTargetById(this.jobId, this.currentSchema);
        this.formData.fortigate_ip = job.fortigate_ip || "";
        this.formData.ssh_port = job.ssh_port || 22;
        this.formData.username = job.username || "splitpoint-ssh";
        this.formData.auth_method = job.auth_method || "password";
        this.formData.collection_interval = job.collection_interval || 3600;
        // Cred fields load empty on edit; user must re-enter to change them.
        for (const field of SENSITIVE_FIELDS) {
          this.formData[field] = "";
        }
      } catch (error) {
        console.error("Error loading SSH collector target:", error);
        alert("Failed to load target data");
        this.goBack();
      }
    },
    handleKeyFile(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.formData.private_key = (e.target.result || "").toString();
        // Clear the file input so the same filename can be re-selected if needed
        if (this.$refs.keyFileInput) {
          this.$refs.keyFileInput.value = "";
        }
      };
      reader.readAsText(file);
    },
    inputClass(field) {
      return [
        'border rounded px-3 py-2 focus:outline-none focus:ring-2 dark:bg-gray-600',
        this.fieldErrors[field] ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300 focus:ring-blue-500'
      ].join(' ');
    },
    goBack() {
      this.$router.push({ name: "SSHCollectorDetails", params: { id: this.configId }, query: { schemaName: this.currentSchema } });
    },
    _buildPayload() {
      const data = {
        ssh_collector_config: parseInt(this.configId),
        fortigate_ip: this.formData.fortigate_ip,
        ssh_port: this.formData.ssh_port,
        username: this.formData.username,
        auth_method: this.formData.auth_method,
        collection_interval: this.formData.collection_interval,
      };
      // Only include cred fields when the user has actually entered something.
      // On edit, empty means "keep existing"; omitting the field preserves it.
      if (this.formData.auth_method === "password") {
        if (this.formData.password) {
          data.password = this.formData.password;
        }
      } else if (this.formData.auth_method === "key") {
        if (this.formData.private_key) {
          data.private_key = this.formData.private_key;
        }
        if (this.formData.private_key_passphrase) {
          data.private_key_passphrase = this.formData.private_key_passphrase;
        }
      }
      return data;
    },
    async submitForm() {
      this.pageError = null;
      this.fieldErrors = {};

      if (!this.formData.fortigate_ip) {
        this.fieldErrors.fortigate_ip = "Fortigate IP is required";
        return;
      }
      if (!this.formData.username) {
        this.fieldErrors.username = "SSH username is required";
        return;
      }
      if (!this.isEditMode) {
        if (this.formData.auth_method === "password" && !this.formData.password) {
          this.fieldErrors.password = "Password is required";
          return;
        }
        if (this.formData.auth_method === "key" && !this.formData.private_key) {
          this.fieldErrors.private_key = "Private key is required";
          return;
        }
      }

      const data = this._buildPayload();

      try {
        if (this.isEditMode) {
          await updateSSHCollectorTarget(this.jobId, data, this.currentSchema);
        } else {
          await createSSHCollectorTarget(data, this.currentSchema);
        }
        // Clear in-memory creds immediately after a successful submit
        for (const field of SENSITIVE_FIELDS) {
          this.formData[field] = "";
        }
        this.goBack();
      } catch (error) {
        this.pageError = null;
        this.fieldErrors = {};

        const errData = error?.response?.data;
        if (errData) {
          Object.keys(errData).forEach((key) => {
            const errorMsg = Array.isArray(errData[key]) ? errData[key][0] : errData[key];
            this.fieldErrors[key] = errorMsg;
          });

          if (errData.ssh_collector_config || errData.non_field_errors) {
            let errorMessage = errData.ssh_collector_config?.[0] || errData.ssh_collector_config ||
                               errData.non_field_errors?.[0] || errData.non_field_errors;

            if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('mimir')) {
              errorMessage = "Your organization tenant is not configured. Please contact Splitpoint support for assistance.";
            }

            this.pageError = errorMessage || "Please correct the errors below";
          } else if (Object.keys(errData).length > 0) {
            this.pageError = "Please correct the errors below";
          }
        } else {
          this.pageError = `Failed to ${this.isEditMode ? 'update' : 'create'} target. Please try again.`;
        }

        console.error(`Error ${this.isEditMode ? 'updating' : 'creating'} SSH collector target:`, error);
      }
    },
  },
};
</script>
